/**
 * Renders an axis value for a diagnostic message.
 *
 * Axis values are unconstrained generics and are routinely objects — Luxon `DateTime` is the
 * standard case, and the equality helpers in the axis services handle it explicitly. A plain
 * `String()` on an object without a meaningful `toString()` yields `"[object Object]"`, which
 * is worthless precisely where it is needed: in the messages that name WHICH value could not
 * be loaded. Those messages exist so a dropped entry stops being invisible; a placeholder
 * would hand the reader back the same silence in a different shape.
 *
 * Order of preference: a useful `toString()` (DateTime, Date, primitives), then JSON, then the
 * placeholder with the reason it could not be rendered.
 *
 * @param value - The value to render
 * @returns A readable representation, never an unexplained "[object Object]"
 */
export function describeValue(value: unknown): string {
    if (value === null || typeof value !== 'object') {
        return String(value);
    }

    const asString = String(value);
    if (asString !== '[object Object]') {
        return asString;
    }

    try {
        const asJson = JSON.stringify(value);

        // JSON.stringify returns undefined WITHOUT throwing when the value has a toJSON()
        // that yields undefined — spec-compliant, and some wrappers around circular
        // structures do exactly that. Falling back silently here would return the bare
        // "[object Object]" this function promises never to hand back unexplained.
        if (asJson === undefined) {
            return `${asString} (not renderable: toJSON() returned undefined)`;
        }

        return asJson;
    } catch (error) {
        // JSON.stringify throws on a circular structure and on BigInt fields. Both mean "not
        // renderable as JSON", neither is a malfunction worth propagating — this only builds a
        // string for a message. Naming the reason keeps the degradation from being invisible.
        const reason = error instanceof Error ? error.message : String(error);

        return `${asString} (not renderable: ${reason})`;
    }
}
